// =============================================================================
// Inventory Management — the outbound queue.
//
// Every delivery across the portfolio in one list, so a truck that should have left last week is
// visible without opening a site. Advancing a delivery here is the same call the site page makes, and
// it is where planning turns into accounting: reaching "delivered" posts the ledger entries.
// =============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import {
  InventoryDeliveryStatus,
  inventoryDeleteDelivery, inventoryDeliveryFlow, inventoryDeliveryStatusLabel, inventoryDeliveryStatuses,
  inventoryListDeliveries, inventorySetDeliveryStatus,
} from '/imports/api/inventory';
import {
  DeliveryStatusChip, EmptyRow, InventoryPageStyled, count, day, orDash, useInventoryAccess,
} from '/imports/ui/InventoryShared';

const DeliveriesPageStyled = styled(InventoryPageStyled)`
  .late { color: ${css.red5}; }
`;

/** Every outbound delivery, newest request first, with the next step one click away. */
export const InventoryDeliveriesPage = () => {
  const { canAccess, canEdit } = useInventoryAccess();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [tick, setTick] = useState(0);
  const [confirm, setConfirm] = useState<{ title: string; body: string; run: () => void }>();
  const [rows, loading] = useMethod(
    inventoryListDeliveries, { status: (status || undefined) as any, search: search || undefined }, [status, search, tick],
  );
  const reload = () => setTick((t) => t + 1);

  const advance = toastOnError(async (deliveryId: string, next: InventoryDeliveryStatus) => {
    const result = await inventorySetDeliveryStatus.call({ deliveryId, status: next });
    toast.success(strings.InventoryDeliveryAdvanced(inventoryDeliveryStatusLabel(next)));
    if (result.reductionsPosted) toast.info(strings.InventoryReductions());
    reload();
  });
  const remove = toastOnError(async (deliveryId: string) => {
    await inventoryDeleteDelivery.call({ deliveryId });
    reload();
  });

  if (!canAccess) return <NotPermittedPage />;

  return (
    <DeliveriesPageStyled className="page">
      <PageHeading left={<h1>{strings.InventoryDeliveries()}</h1>} />
      <PageBody>
        <div className="inv-bar">
          <Input className="search" value={search} placeholder={strings.InventorySearchSitesPlaceholder()} onChange={(v) => setSearch(v || '')} />
          <Select
            value={status}
            options={[{ value: '', label: strings.InventoryAllDeliveryStates() }, ...inventoryDeliveryStatuses.map((s) => ({ value: s, label: inventoryDeliveryStatusLabel(s) }))]}
            onChange={setStatus}
          />
          <span className="spacer" />
          <span className="meta">{count(rows?.length)}</span>
        </div>

        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventorySiteId()}</th>
                <th>{strings.InventoryPropertyName()}</th>
                <th>{strings.Status()}</th>
                <th className="num">{strings.InventoryPallet()}</th>
                <th className="num">{strings.InventoryModules()}</th>
                <th className="num">{strings.InventoryTrucks()}</th>
                <th>{strings.InventoryWarehouse()}</th>
                <th>{strings.InventoryRequestedAt()}</th>
                <th>{strings.InventoryRequestedBy()}</th>
                <th>{strings.InventoryMobilizationDate()}</th>
                <th>{strings.InventoryShipDate()}</th>
                <th>{strings.InventoryArrivedAt()}</th>
                <th>{strings.InventoryArrivalVsMobilization()}</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {loading && !rows && <tr><td colSpan={14}><LoadingFlex /></td></tr>}
              {(rows || []).map((d) => {
                const next = inventoryDeliveryFlow[inventoryDeliveryFlow.indexOf(d.status) + 1];
                const late = (d.arrivalVsMobilizationDays || 0) > 0;
                return (
                  <tr key={d._id}>
                    <td><Link to={`/inventory/sites/${d.siteId}`}>{d.siteId}</Link></td>
                    <td>{orDash(d.siteName)}</td>
                    <td><DeliveryStatusChip status={d.status} /></td>
                    <td className="num">{count(d.pallets)}</td>
                    <td className="num">{count(d.modules)}</td>
                    <td className="num">{count(d.trucks)}</td>
                    <td>{orDash(d.warehouseCodes.join(', '))}</td>
                    <td>{day(d.requestedAt)}</td>
                    <td>{orDash(d.requestedByName)}</td>
                    <td>{day(d.scheduledMobilizationDate)}</td>
                    <td>{day(d.actualShipDate)}</td>
                    <td className={late ? 'late' : ''}>{day(d.arrivedAt)}</td>
                    <td className={late ? 'late' : ''}>
                      {d.arrivalVsMobilizationDays == null ? orDash(undefined)
                        : d.arrivalVsMobilizationDays > 0 ? strings.InventoryDaysLate(d.arrivalVsMobilizationDays)
                          : strings.InventoryDaysEarly(-d.arrivalVsMobilizationDays)}
                    </td>
                    {canEdit && (
                      <td>
                        {next && <button className="link-btn" onClick={() => advance(d._id, next)}>{inventoryDeliveryStatusLabel(next)}</button>}
                        {inventoryDeliveryFlow.indexOf(d.status) < inventoryDeliveryFlow.indexOf('inTransit') && (
                          <>
                            {next && <span className="muted"> | </span>}
                            <button
                              className="link-btn"
                              onClick={() => setConfirm({ title: strings.InventoryDeleteDelivery(), body: `${d.siteId} — ${d.siteName || ''}`, run: () => remove(d._id) })}
                            >
                              {strings.Delete()}
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {!loading && !rows?.length && <EmptyRow colSpan={14} label={strings.InventoryNoDeliveries()} />}
            </tbody>
          </table>
        </div>
      </PageBody>
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          onConfirm={() => { confirm.run(); setConfirm(undefined); }}
          onClose={() => setConfirm(undefined)}
        />
      )}
    </DeliveriesPageStyled>
  );
};
